import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * `size="sm"` — denser gap/padding (dashboard density option).
 * `flush` — zeroes Card's own py-5 so the body reaches the card's top/bottom
 * edge; use for a full-bleed body that owns its own padding, e.g. onboarding
 * step 1's two-pane grid (`step-domain.tsx`). Never override with a
 * call-site `className="py-0"` (DES-50) — that drifts the moment `Card`'s
 * base spacing changes.
 */
function Card({
  className,
  size = 'default',
  flush = false,
  ...props
}: React.ComponentProps<'div'> & { size?: 'default' | 'sm'; flush?: boolean }) {
  return (
    <div
      data-slot="card"
      data-size={size}
      data-flush={flush ? 'true' : undefined}
      className={cn(
        // Codex §4/§3: hairline border, NO shadow, radius via --radius-card
        // (12px, brand-token-migration T1 — see untitledui-reference.md's
        // "Brand-token migration" subsection), p-5 (20px) spirit. Uses the
        // named `rounded-card` utility directly rather than the `rounded-lg`
        // deprecation alias (`--radius-lg` → `--radius-card`) T1 keeps live
        // for the ~43 un-migrated call sites — this file IS being migrated.
        // `flush`: zeroes Card's own vertical py-5 for a full-bleed body that owns its
        // own edge-to-edge padding (e.g. a two-pane grid with per-panel padding, or a
        // panel with a divider that must reach the card's border) — the same idiom as
        // `has-[>img:first-child]:pt-0` above, generalized to any content, not just a
        // leading image, and to both edges rather than only the top. Reach for this
        // instead of a call-site `className="py-0"` override (DES-50): a one-off
        // override drifts from the component the next redesign touches, this doesn't.
        'group/card flex flex-col gap-4 overflow-hidden rounded-card bg-card py-5 text-sm text-card-foreground ring-1 ring-border has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0 data-[flush=true]:py-0 data-[size=sm]:gap-3 data-[size=sm]:py-3 data-[size=sm]:has-data-[slot=card-footer]:pb-0 *:[img:first-child]:rounded-t-card *:[img:last-child]:rounded-b-card',
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        // Codex §5: card padding p-5 (20px); px-5 horizontal to match
        'group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-t-card px-5 group-data-[size=sm]/card:px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-4 group-data-[size=sm]/card:[.border-b]:pb-3',
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        // Codex §1: card titles 17px/600/tracking-tight via text-card-title token
        // (round-4 audit: corrected 15px/700 → 17px/600 per the design source)
        'font-heading text-card-title group-data-[size=sm]/card:text-sm',
        className
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-sm leading-relaxed text-muted-foreground tracking-[-0.01em]', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-5 group-data-[size=sm]/card:px-4', className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  // round-4 audit: the design source's literal card-footer specimen ("Footer
  // strip — paper-50, hairline top") — was bg-muted/50, the old, unmigrated
  // --gray-100 primitive. --paper-50 is theme-invariant (stays #fbfbfc in
  // dark), so the strip uses the theme-aware `--card-footer` token (globals.css:
  // paper-50 light, d-canvas dark, mkt-canvas in dark marketing) — otherwise
  // the footer's light-on-light text fails contrast (axe 1.93:1 on /pricing).
  // A plain bg-* utility, not a `dark:` variant, so a caller's own bg-* wins.
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'flex items-center rounded-b-card border-t bg-card-footer p-4 group-data-[size=sm]/card:p-3',
        className
      )}
      {...props}
    />
  )
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent }
